
import React from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '@/components/utils/PageWrapper';
import AnimatedElement from '@/components/utils/AnimatedElement';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowUpRight, BarChart2, Brain, Cpu, Database, Server, Bot, Network, Cloud, Factory, Target, Layers, Camera, ScanLine } from 'lucide-react';
import { motion } from 'framer-motion';

const serviceCategories = [
  {
    id: 'data-analytics',
    title: 'Data Analytics',
    icon: <BarChart2 className="h-10 w-10" />,
    color: 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400',
    description: 'Transform raw data into actionable insights with our advanced analytics solutions.',
    features: [
      'Business Intelligence Dashboards',
      'Predictive Analytics',
      'Data Visualization',
      'Real-time Analytics',
      'ETL Processes',
      'Custom KPI Tracking'
    ]
  },
  {
    id: 'machine-learning',
    title: 'Machine Learning',
    icon: <Brain className="h-10 w-10" />,
    color: 'bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400',
    description: 'Custom ML models that learn from your data to make predictions and automate decisions.',
    features: [
      'Supervised Learning',
      'Unsupervised Learning',
      'Deep Learning',
      'Natural Language Processing',
      'Computer Vision',
      'Reinforcement Learning'
    ]
  },
  {
    id: 'iot-solutions',
    title: 'IoT Solutions',
    icon: <Cpu className="h-10 w-10" />,
    color: 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400',
    description: 'Connect devices and systems to create intelligent networks that deliver real-time data.',
    features: [
      'IoT Architecture Design',
      'Sensor Networks',
      'Edge Computing',
      'Real-time Monitoring',
      'Computer Vision',
      'Robotics Automation',
      'Industrial IoT (IIoT)',
      'Smart Device Integration'
    ]
  },
  {
    id: 'artificial-intelligence',
    title: 'Artificial Intelligence',
    icon: <Bot className="h-10 w-10" />,
    color: 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400',
    description: 'Advanced AI systems that solve complex problems and enhance decision-making processes.',
    features: [
      'Cognitive Computing',
      'Expert Systems',
      'Autonomous Agents',
      'Knowledge Representation',
      'AI Strategy Consulting',
      'Process Automation'
    ]
  },
  {
    id: 'cloud-infrastructure',
    title: 'Cloud Infrastructure',
    icon: <Cloud className="h-10 w-10" />,
    color: 'bg-cyan-50 dark:bg-cyan-950 text-cyan-600 dark:text-cyan-400',
    description: 'Scalable cloud solutions to store, process, and analyze massive data sets efficiently.',
    features: [
      'Cloud Migration',
      'Hybrid Cloud Solutions',
      'Multi-cloud Strategy',
      'Serverless Architecture',
      'Container Orchestration',
      'Cloud Security'
    ]
  },
  {
    id: 'industrial-automation',
    title: 'Intelligent Industrial Automation',
    icon: <Factory className="h-10 w-10" />,
    color: 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400',
    description: 'Smart manufacturing and production systems powered by AI and IoT technologies.',
    features: [
      'Smart Manufacturing',
      'Process Optimization',
      'Predictive Maintenance',
      'Quality Control Systems',
      'Digital Twins',
      'Supply Chain Automation'
    ]
  },
  {
    id: 'autonomous-agents',
    title: 'Autonomous Agents',
    icon: <Target className="h-10 w-10" />,
    color: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400',
    description: 'Self-learning systems that operate independently to accomplish complex tasks.',
    features: [
      'Autonomous Decision Making',
      'Multi-agent Systems',
      'Self-healing Networks',
      'Robotic Process Automation',
      'Swarm Intelligence',
      'Intelligent Assistants'
    ]
  },
  {
    id: 'ai-infrastructure',
    title: 'AI Infrastructure',
    icon: <Server className="h-10 w-10" />,
    color: 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400',
    description: 'Robust hardware and software frameworks designed specifically for AI workloads.',
    features: [
      'GPU Clusters',
      'TPU Deployment',
      'High-Performance Computing',
      'AI-optimized Storage',
      'MLOps Platforms',
      'Distributed Training'
    ]
  },
  {
    id: 'ai-models',
    title: 'AI Models | Foundational Models',
    icon: <Layers className="h-10 w-10" />,
    color: 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400',
    description: 'Pre-trained and custom foundational models to power your AI applications.',
    features: [
      'Large Language Models',
      'Vision Transformers',
      'Multimodal Models',
      'Model Fine-tuning',
      'Model Optimization',
      'Custom Model Development'
    ]
  },
  {
    id: 'computer-vision',
    title: 'Computer Vision',
    icon: <Camera className="h-10 w-10" />,
    color: 'bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400',
    description: 'Advanced systems that enable machines to interpret and understand visual information from the world.',
    features: [
      'Object Detection & Recognition',
      'Image Classification',
      'Visual Search & Retrieval',
      'Video Analytics',
      'Pose Estimation',
      'Optical Character Recognition',
      'Facial Recognition',
      'Scene Understanding'
    ]
  },
  {
    id: 'robotics-automation',
    title: 'Robotics Automation',
    icon: <ScanLine className="h-10 w-10" />,
    color: 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400',
    description: 'Intelligent robotic systems that automate complex tasks and processes with precision and adaptability.',
    features: [
      'Collaborative Robots (Cobots)',
      'Autonomous Mobile Robots',
      'Industrial Robotic Systems',
      'Robotic Process Automation',
      'Robot Vision Systems',
      'Human-Robot Collaboration',
      'Robotic Simulation & Planning',
      'Custom Robotic Solutions'
    ]
  }
];

const Services = () => {
  return (
    <PageWrapper
      title="Complete AI Solutions"
      subtitle="Transforming businesses worldwide with cutting-edge artificial intelligence"
    >
      <div className="container mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {serviceCategories.map((service, index) => (
            <AnimatedElement key={service.id} animation="fadeIn" delay={index * 0.05}>
              <motion.div
                whileHover={{ y: -8, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="h-full"
              >
                <Card 
                  className="border border-gray-100 dark:border-gray-800 hover:shadow-prominent transition-all duration-300 h-full flex flex-col"
                >
                  <CardHeader>
                    <motion.div 
                      className={`${service.color} p-3 rounded-xl inline-block mb-4`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 500, damping: 10 }}
                    >
                      {service.icon}
                    </motion.div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-grow">
                    <ul className="space-y-2 mb-4 flex-grow">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.div
                      whileHover={{ x: 3, y: -3 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      className="mt-auto"
                    >
                      <Link
                        to={`/services/${service.id}`}
                        className="inline-flex items-center text-primary font-medium hover-lift"
                      >
                        <span className="relative link-hover-effect">
                          Learn more
                        </span>
                        <ArrowUpRight className="ml-1 h-4 w-4 transform transition-transform duration-300" />
                      </Link>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatedElement>
          ))}
        </div>

        <AnimatedElement animation="slideUp" delay={0.2} className="mb-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold">Featured Solutions</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mt-2">
              Explore our most advanced AI and data solutions in detail
            </p>
          </div>
          
          <Tabs defaultValue="ai" className="max-w-4xl mx-auto">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-8">
              <TabsTrigger value="ai">AI Solutions</TabsTrigger>
              <TabsTrigger value="data">Data Analytics</TabsTrigger>
              <TabsTrigger value="cloud">Cloud Services</TabsTrigger>
              <TabsTrigger value="industry">Industry Solutions</TabsTrigger>
            </TabsList>
            
            <TabsContent value="ai" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">AI Transformation</h3>
                  <p className="text-muted-foreground mb-4">
                    Comprehensive AI strategy and implementation to transform your business operations and create new opportunities.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Database className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Data-Driven Decision Making</span>
                    </li>
                    <li className="flex items-center">
                      <Brain className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Custom AI Model Development</span>
                    </li>
                    <li className="flex items-center">
                      <Target className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Business Process Automation</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-red-50 dark:from-amber-950/30 dark:to-red-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">Foundational Models</h3>
                  <p className="text-muted-foreground mb-4">
                    Leverage the power of advanced foundation models to create intelligent applications.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Layers className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Large Language Models</span>
                    </li>
                    <li className="flex items-center">
                      <Network className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Multi-modal AI Systems</span>
                    </li>
                    <li className="flex items-center">
                      <Bot className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Domain-specific Fine-tuning</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="data" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">Advanced Analytics</h3>
                  <p className="text-muted-foreground mb-4">
                    Transform raw data into actionable insights with our comprehensive analytics solutions.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <BarChart2 className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Predictive Analytics</span>
                    </li>
                    <li className="flex items-center">
                      <Database className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Big Data Processing</span>
                    </li>
                    <li className="flex items-center">
                      <Target className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Custom Dashboards & Visualizations</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">Data Engineering</h3>
                  <p className="text-muted-foreground mb-4">
                    Build robust data pipelines and infrastructure for seamless data operations.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Database className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">ETL Pipeline Development</span>
                    </li>
                    <li className="flex items-center">
                      <Server className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Data Warehousing</span>
                    </li>
                    <li className="flex items-center">
                      <Cloud className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Real-time Data Processing</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="cloud" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-cyan-50 to-indigo-50 dark:from-cyan-950/30 dark:to-indigo-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">Cloud Architecture</h3>
                  <p className="text-muted-foreground mb-4">
                    Design and implement scalable, resilient cloud infrastructure for your business.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Cloud className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Multi-cloud Strategy</span>
                    </li>
                    <li className="flex items-center">
                      <Server className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Serverless Computing</span>
                    </li>
                    <li className="flex items-center">
                      <Database className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Database Optimization</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">AI Infrastructure</h3>
                  <p className="text-muted-foreground mb-4">
                    Specialized infrastructure solutions optimized for AI and ML workloads.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Cpu className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">GPU/TPU Clusters</span>
                    </li>
                    <li className="flex items-center">
                      <Server className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">High-Performance Computing</span>
                    </li>
                    <li className="flex items-center">
                      <Layers className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">MLOps Pipelines</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="industry" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-red-50 to-amber-50 dark:from-red-950/30 dark:to-amber-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">Industrial Automation</h3>
                  <p className="text-muted-foreground mb-4">
                    Intelligent automation solutions for manufacturing and industrial environments.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Factory className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Smart Manufacturing</span>
                    </li>
                    <li className="flex items-center">
                      <Cpu className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Industrial IoT</span>
                    </li>
                    <li className="flex items-center">
                      <Target className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Predictive Maintenance</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold mb-3">Autonomous Systems</h3>
                  <p className="text-muted-foreground mb-4">
                    Self-operating systems that learn, adapt, and make decisions independently.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center">
                      <Bot className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Robotic Process Automation</span>
                    </li>
                    <li className="flex items-center">
                      <Brain className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Intelligent Decision Systems</span>
                    </li>
                    <li className="flex items-center">
                      <Network className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Autonomous Agents</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </AnimatedElement>

        <AnimatedElement animation="slideUp" delay={0.3}>
          <div className="text-center mt-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              Ready to Transform Your Business with AI?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Contact our team today to discuss how our solutions can help you achieve your business goals.
            </p>
            <Link
              to="/contact"
              className="glass-button-primary px-6 py-3 text-base font-medium inline-block"
            >
              Get in Touch
              <ArrowUpRight className="ml-2 h-4 w-4 inline-block" />
            </Link>
          </div>
        </AnimatedElement>
      </div>
    </PageWrapper>
  );
};

export default Services;
